import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { CdkPortal, ComponentPortal } from '@angular/cdk/portal';
import { Injectable, InjectionToken, Injector, Type, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { TemplateModalOverlayRef } from './modal.component';

export interface ModalSize {
  width?: string;
  height?: string;
  minWidth?: string;
  minHeight?: string;
  maxWidth?: string;
  maxHeight?: string;
}

export interface ModalPosition {
  top?: string;
  left?: string;
  right?: string;
  bottom?: string;
}

export interface ModalConfig {
  panelClass?: string | string[];
  hasBackdrop?: boolean;
  backdropClass?: string | string[];
  disableClose?: boolean;
  width?: string;
  height?: string;
  minWidth?: string;
  minHeight?: string;
  maxWidth?: string;
  maxHeight?: string;
  position?: ModalPosition;
  data?: any;
  id?: string;
  autoFocus?: boolean;
  restoreFocus?: boolean;
  topClose?: boolean;
  hideCloseButton?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'material-modal';
}

export interface CustomOverlayConfig {
  hasBackdropClick?: boolean;
  isCentered?: boolean;
  size?: ModalSize;
  position?: ModalPosition;
  topClose?: boolean;
}

export interface ModalRef<T = any> {
  id: string;
  componentInstance?: T;
  close(result?: any): void;
  afterClosed(): Observable<any>;
  afterOpened(): Observable<any>;
  backdropClick(): Observable<MouseEvent>;
  keydownEvents(): Observable<KeyboardEvent>;
}

const DEFAULT_CONFIG: ModalConfig = {
  hasBackdrop: true,
  disableClose: false,
  autoFocus: true,
  restoreFocus: true,
  topClose: true,
  hideCloseButton: false,
  size: undefined
};

const DEFAULT_CUSTOM_CONFIG: CustomOverlayConfig = {
  hasBackdropClick: true,
  isCentered: true,
  size: undefined,
  topClose: true
};

export const MODAL_DATA = new InjectionToken<any>('MODAL_DATA');
export const MODAL_REF = new InjectionToken<ModalRef>('MODAL_REF');
export const MODAL_CONFIG = new InjectionToken<ModalConfig>('MODAL_CONFIG');

@Injectable({
  providedIn: 'root'
})
export class TemplateModalService {
  private injector = inject(Injector);
  private overlay = inject(Overlay);

  private openModals = new Map<string, ModalRef>();
  private modalIdCounter = 0;

  /**
   * Opens a modal from a template reference (existing functionality)
   */
  open(templateRef: CdkPortal, config: ModalConfig = {}, customConfig: CustomOverlayConfig = {}): any {
    const modalId = this.generateModalId();
    const mergedConfig = { ...DEFAULT_CONFIG, ...config };
    const mergedCustomConfig = { ...DEFAULT_CUSTOM_CONFIG, ...customConfig };

    const overlayRef = this.createOverlay(mergedConfig, mergedCustomConfig);
    const modalRef = new TemplateModalOverlayRef(overlayRef, modalId);

    this.attachTemplatePortal(overlayRef, mergedConfig, modalRef, templateRef);
    this.setupModalEvents(overlayRef, modalRef, mergedCustomConfig);

    this.openModals.set(modalId, modalRef);
    return modalRef;
  }

  /**
   * Opens a modal from an Angular component
   */
  openComponent<T>(component: Type<T>, config: ModalConfig = {}): ModalRef<T> {
    const modalId = this.generateModalId();
    const mergedConfig = { ...DEFAULT_CONFIG, ...config };

    const overlayRef = this.createOverlay(mergedConfig);
    const modalRef = new TemplateModalOverlayRef(overlayRef, modalId);

    const componentRef = this.attachComponentPortal(overlayRef, mergedConfig, modalRef, component);
    modalRef.componentInstance = componentRef.instance;

    this.setupModalEvents(overlayRef, modalRef);
    this.openModals.set(modalId, modalRef);

    return modalRef;
  }

  /**
   * Opens a modal with data injection
   */
  openWithData<T>(component: Type<T>, data: any, config: ModalConfig = {}): ModalRef<T> {
    // Merge data into config, but prioritize config values over data values
    const configWithData = { ...data, ...config, data };
    return this.openComponent(component, configWithData);
  }

  /**
   * Closes a modal by ID
   */
  close(modalId: string, result?: any): void {
    const modalRef = this.openModals.get(modalId);
    if (modalRef) {
      modalRef.close(result);
      this.openModals.delete(modalId);
    }
  }

  /**
   * Closes all open modals
   */
  closeAll(): void {
    this.openModals.forEach((modalRef) => {
      modalRef.close();
    });
    this.openModals.clear();
  }

  /**
   * Gets the number of open modals
   */
  getOpenModalCount(): number {
    return this.openModals.size;
  }

  /**
   * Gets all open modal IDs
   */
  getOpenModalIds(): string[] {
    return Array.from(this.openModals.keys());
  }

  /**
   * Checks if a modal is open
   */
  isModalOpen(modalId: string): boolean {
    return this.openModals.has(modalId);
  }

  private generateModalId(): string {
    return `modal-${++this.modalIdCounter}`;
  }

  private attachTemplatePortal(
    overlayRef: OverlayRef,
    config: ModalConfig,
    modalRef: TemplateModalOverlayRef,
    templateRef: CdkPortal
  ) {
    const injector = this.createInjector(config, modalRef);
    return overlayRef.attach(templateRef);
  }

  private attachComponentPortal<T>(
    overlayRef: OverlayRef,
    config: ModalConfig,
    modalRef: TemplateModalOverlayRef,
    component: Type<T>
  ) {
    const injector = this.createInjector(config, modalRef);
    const portal = new ComponentPortal(component, null, injector);
    return overlayRef.attach(portal);
  }

  private createInjector(config: ModalConfig, modalRef: TemplateModalOverlayRef): Injector {
    const providers = [
      { provide: TemplateModalOverlayRef, useValue: modalRef },
      { provide: MODAL_REF, useValue: modalRef },
      { provide: MODAL_CONFIG, useValue: config }
    ];

    if (config.data !== undefined) {
      providers.push({ provide: MODAL_DATA, useValue: config.data });
    }

    return Injector.create({
      providers,
      parent: this.injector
    });
  }

  private setupModalEvents(
    overlayRef: OverlayRef,
    modalRef: TemplateModalOverlayRef,
    customConfig?: CustomOverlayConfig
  ) {
    // Backdrop click handling
    overlayRef.backdropClick().subscribe((event) => {
      if (customConfig?.hasBackdropClick !== false) {
        modalRef.close();
      }
    });

    // Escape key handling
    overlayRef.keydownEvents().subscribe((event) => {
      if (event.key === 'Escape') {
        modalRef.close();
      }
    });

    // Cleanup when overlay is disposed
    overlayRef.detachments().subscribe(() => {
      this.openModals.delete(modalRef.id);
    });

    // Cleanup on close
    modalRef.afterClosed().subscribe(() => {
      this.openModals.delete(modalRef.id);
    });
  }

  private getOverlayConfig(config: ModalConfig, customConfig?: CustomOverlayConfig): OverlayConfig {
    let positionStrategy = this.overlay.position().global();

    // Apply centering
    if (customConfig?.isCentered !== false) {
      positionStrategy = positionStrategy.centerHorizontally().centerVertically();
    }

    // Apply custom positioning
    if (customConfig?.position) {
      const pos = customConfig.position;
      if (pos.top) positionStrategy = positionStrategy.top(pos.top);
      if (pos.left) positionStrategy = positionStrategy.left(pos.left);
      if (pos.right) positionStrategy = positionStrategy.right(pos.right);
      if (pos.bottom) positionStrategy = positionStrategy.bottom(pos.bottom);
    }

    // Apply size constraints from custom config
    if (customConfig?.size) {
      const size = customConfig.size;
      if (size.width) positionStrategy = positionStrategy.width(size.width);
      if (size.height) positionStrategy = positionStrategy.height(size.height);
    }

    // Apply size constraints from main config
    if (config.width) positionStrategy = positionStrategy.width(config.width);
    if (config.height) positionStrategy = positionStrategy.height(config.height);

    // Apply size constraints from size enum
    if (config.size) {
      const sizeDimensions = this.getSizeDimensions(config.size);
      if (sizeDimensions.width) positionStrategy = positionStrategy.width(sizeDimensions.width);
      if (sizeDimensions.height) positionStrategy = positionStrategy.height(sizeDimensions.height);
    }

    return new OverlayConfig({
      hasBackdrop: config.hasBackdrop,
      backdropClass: config.backdropClass,
      panelClass: config.panelClass,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy,
      disposeOnNavigation: true
    });
  }

  private getSizeDimensions(size: 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'material-modal'): { width?: string; height?: string } {
    switch (size) {
      case 'sm':
        return { width: '400px', height: '400px' };
      case 'md':
        return { width: '500px', height: '500px' };
      case 'lg':
        return { width: '700px', height: '700px' };
      case 'xl':
        return { width: '900px', height: '900px' };
      case 'full':
        return { width: '95vw', height: '95vh' };
      case 'material-modal':
        return { width: '600px', height: '60%' };
      default:
        return {};
    }
  }

  private createOverlay(config: ModalConfig, customConfig?: CustomOverlayConfig): OverlayRef {
    const overlayConfig = this.getOverlayConfig(config, customConfig);
    return this.overlay.create(overlayConfig);
  }
}
